// Every notification kind the database writes → how the app names it, which
// icon it carries, and where opening it goes (WS-08).
//
// Two components each kept part of this: notifications.tsx had a six-entry
// KIND_LABEL (one of them, `leave_decided`, is written by nothing) and fell back
// to the raw id, so members read "correction_approved" under every row; the bell
// and the tab each copied a localStorage write to land bug reports on their tab.
// And the triggers link approvers to a bare '/hr', which opens the dashboard,
// not the request.
//
// ADDING A NOTIFY TRIGGER? Add its kind here in the same change. The kinds below
// are every literal in `insert into public.notifications` across
// supabase/migrations, with the status-derived ones expanded from the check
// constraints ('pending','approved','rejected'; comp hours add 'revoked'):
//   leave_<status>             20260908100000_leave_ledger.sql (latest writer)
//   leave_requested            same
//   leave_clash, leave_clash_hr 20260908110000_leave_ledger_writers.sql
//   correction_requested, correction_<status>  20260825100000_time_corrections.sql
//   day_auto_closed            20261013100000_fix_auto_closed_day.sql (latest writer)
//   day_fixed                  same
//   day_edited, day_edit_requested, day_edit_<status>  20261103120000_edit_day.sql
//   comp_hours_requested, comp_hours_<status>, comp_hours_credited,
//   comp_hours_revoked         20260922110000_comp_hours_revoke.sql
//   key_date_due, payroll_reconcile_due        20260829140000_documents_gates.sql
//   bug_reported               20260805110000_bug_reports.sql
//   bug_screenshots_expired    20260812110000_retention.sql
//   event_posted, event_rescheduled, event_cancelled  20260917120000_calendar_events.sql
//   daily_report_sent          20261015163000_send_daily_report.sql
//   request_escalated          20261015170000_escalations.sql
//   policy_published           20261015171000_policies.sql
//   certificate_expiring, certificate_expired  20261015172000_certificates.sql
//   task_assigned, task_mention, task_comment, task_status  20261016110000_issues_activity.sql
//   post_announcement, post_blocker, post_mention  20261019140000_project_posts.sql
// `correction_pending` / `comp_hours_pending` are never written (both triggers
// notify only on a decision); `leave_pending` is — a decision reverted.
//
// Labels follow the Vocabulary in docs/UI-CONVENTIONS.md: Approved / Declined,
// never "rejected", which is only the stored value.
import type { IconName } from '../components/primitives';

export type NotificationKind = {
  label: string;
  icon: IconName;
  /**
   * Where a bare server link should really go. Applied only when the row's link
   * is exactly `path` — a link that already carries a query (calendar's
   * `?tab=calendar&date=…`) or a different path (a manager's '/me') is precise
   * already and passes through, so a later migration that writes exact links
   * needs no change here.
   */
  route?: { path: string; query: string };
};

// Approvers open the Requests & approvals view, not the People dashboard.
const HR_REQUESTS = { path: '/hr', query: 'view=requests' };
// Org reads ?tab= (OrgSettingsScreen), which replaces the hf.org.tab side-channel.
const ORG_BUGS = { path: '/org', query: 'tab=bugs' };

export const NOTIFICATION_KINDS: Record<string, NotificationKind> = {
  // Leave — to the requester ('/me') or to HR ('/hr').
  leave_requested: { label: 'Leave request', icon: 'leave', route: HR_REQUESTS },
  leave_approved: { label: 'Leave approved', icon: 'check' },
  leave_rejected: { label: 'Leave declined', icon: 'x' },
  leave_pending: { label: 'Leave decision reverted', icon: 'leave' },
  leave_clash: { label: 'Leave adjusted', icon: 'leave' },
  // To HR, about someone else's balance; it has no request to open, so no route.
  leave_clash_hr: { label: 'Worked on leave', icon: 'leave' },

  // Time corrections — to HR, then back to the requester on /timesheets.
  correction_requested: { label: 'Time correction request', icon: 'clock', route: HR_REQUESTS },
  correction_approved: { label: 'Time correction approved', icon: 'check' },
  correction_rejected: { label: 'Time correction declined', icon: 'x' },
  // A day nobody checked out of, closed by close_stale_sessions — to the person
  // ('/timesheets'), their manager and people:full ('/hr?view=details&person=…').
  // Both links are exact already, so no route.
  day_auto_closed: { label: 'Day closed automatically', icon: 'clock' },
  // Your manager or HR set the real times of that day (20261013100000), to
  // '/timesheets'. Exact already, so no route.
  day_fixed: { label: 'Day’s times set', icon: 'clock' },
  // Edit day (20261103120000): HR or the manager changed your day, to
  // '/timesheets'; a whole-day request to its deciders ('/hr?view=requests' for
  // HR, '/me' for a manager), then its decision back to '/timesheets'. All exact.
  day_edited: { label: 'Day changed', icon: 'clock' },
  day_edit_requested: { label: 'Day change request', icon: 'clock' },
  day_edit_approved: { label: 'Day change approved', icon: 'check' },
  day_edit_rejected: { label: 'Day change declined', icon: 'x' },

  // Compensated hours — to the reporting manager ('/me', kept as is) or, with
  // no live manager, to HR ('/hr', routed to the queue).
  comp_hours_requested: { label: 'Compensated hours claim', icon: 'clock', route: HR_REQUESTS },
  comp_hours_approved: { label: 'Compensated hours approved', icon: 'check' },
  comp_hours_rejected: { label: 'Compensated hours declined', icon: 'x' },
  comp_hours_credited: { label: 'Compensated hours credited', icon: 'award' },
  comp_hours_revoked: { label: 'Compensated hours revoked', icon: 'x' },
  // A month's overtime, credited as comp off when payroll locks (20261008110000).
  // Not "credited": beside "Compensated hours credited" that read as one credit.
  overtime_credited: { label: 'Comp off earned', icon: 'leave' },

  // Nightly sweeps, to people:full. Lapsed key dates are a Requests queue
  // (lib/attention.ts); an open payroll month is closed from Payroll.
  key_date_due: { label: 'Key date due', icon: 'calendar', route: HR_REQUESTS },
  // A leave request or correction waiting past the org's limit (FR-8.5), to the
  // approvers and their managers; it opens the queue it is waiting in.
  request_escalated: { label: 'Request waiting too long', icon: 'inbox', route: HR_REQUESTS },
  payroll_reconcile_due: {
    label: 'Payroll month still open',
    icon: 'lock',
    route: { path: '/hr', query: 'view=payroll' },
  },

  // Bug reports — to org:full.
  bug_reported: { label: 'Bug report', icon: 'inbox', route: ORG_BUGS },
  bug_screenshots_expired: {
    label: 'Bug screenshots past retention',
    icon: 'inbox',
    route: ORG_BUGS,
  },

  // A policy, or a new version of one, to read and acknowledge (FR-8.7). The
  // link is '/me', where it is acknowledged; exact already, so no route.
  policy_published: { label: 'Policy to acknowledge', icon: 'file' },
  // A certificate 30 or 7 days from expiry, or expired (FR-8.6): to the person
  // ('/me') and HR (the person's Documents tab). Both links are exact already.
  certificate_expiring: { label: 'Certificate expiring', icon: 'calendar' },
  certificate_expired: { label: 'Certificate expired', icon: 'calendar' },

  // Calendar — the link already names the day.
  event_posted: { label: 'New event', icon: 'calendar' },
  event_rescheduled: { label: 'Event moved', icon: 'calendar' },
  event_cancelled: { label: 'Event cancelled', icon: 'calendar' },

  // A report sent up the chain — to the author's direct manager, on '/me',
  // where Team reports lists it.
  daily_report_sent: { label: 'Daily report', icon: 'file' },
  // Work items (20261016110000) — the link opens the item (/project?id=…&issue=KEY).
  task_assigned: { label: 'Assigned to you', icon: 'user' },
  task_mention: { label: 'You were mentioned', icon: 'users' },
  task_comment: { label: 'New comment', icon: 'inbox' },
  task_status: { label: 'Work item moved', icon: 'layers' },
  // A project's Posts (20261019140000) — the link opens the post
  // (/project?id=…&tab=posts&post=…). Updates and Milestones notify nobody.
  post_announcement: { label: 'Announcement', icon: 'zap' },
  post_blocker: { label: 'Blocker posted', icon: 'stop' },
  post_mention: { label: 'You were mentioned in a post', icon: 'users' },
};

/**
 * The label for a kind. An unknown one — a trigger added without an entry here —
 * is humanised ("payslip_ready" → "Payslip ready"), never shown raw.
 */
export const kindLabel = (kind: string): string => {
  const known = NOTIFICATION_KINDS[kind]?.label;
  if (known) return known;
  const words = kind.replace(/[_-]+/g, ' ').trim();
  return words ? words[0].toUpperCase() + words.slice(1) : 'Notification';
};

/** The icon for a kind; the bell for one this map has not caught up with. */
export const kindIcon = (kind: string): IconName => NOTIFICATION_KINDS[kind]?.icon ?? 'bell';

/** Where opening a notification goes, or null when it has nowhere to go. */
export const notificationRoute = (n: { kind: string; link: string | null }): string | null => {
  if (!n.link) return null;
  const route = NOTIFICATION_KINDS[n.kind]?.route;
  return route && n.link === route.path ? `${route.path}?${route.query}` : n.link;
};
